import React, { useState } from "react";
import { AlertTriangle, CheckCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export function OpenRegisterModal({
  isOpen,
  setIsOpen,
  openCashRegister,
}: {
  isOpen: boolean;
  setIsOpen: (val: boolean) => void;
  openCashRegister: any;
}) {
  const [initialBalance, setInitialBalance] = useState("200.00");

  const handleOpenRegister = (e: React.FormEvent) => {
    e.preventDefault();
    openCashRegister.mutate(Number(initialBalance), {
      onSuccess: () => setIsOpen(false),
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="sm:max-w-[425px] bg-slate-900 border-slate-700 text-slate-100">
        <DialogHeader>
          <DialogTitle>Abrir Novo Caixa</DialogTitle>
          <DialogDescription className="text-slate-400">
            Informe o saldo inicial (fundo de gaveta) para abrir a sessão.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleOpenRegister} className="space-y-4 pt-4">
          <div className="space-y-2">
            <Label>Saldo Inicial (R$)</Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              value={initialBalance}
              onChange={(e) => setInitialBalance(e.target.value)}
              className="bg-slate-800 border-slate-700 font-bold text-lg"
              required
            />
          </div>
          <Button
            type="submit"
            className="w-full bg-emerald-600 hover:bg-emerald-700 font-bold"
            disabled={openCashRegister.isPending}
          >
            {openCashRegister.isPending ? (
              <Loader2 className="animate-spin" />
            ) : (
              "Confirmar Abertura"
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function CloseRegisterModal({
  isOpen,
  setIsOpen,
  pendingCommissions,
  expectedTotal,
  closeCashRegister,
}: {
  isOpen: boolean;
  setIsOpen: (val: boolean) => void;
  pendingCommissions: number;
  expectedTotal: number;
  closeCashRegister: any;
}) {
  const [countedPhysical, setCountedPhysical] = useState("");
  const [countedDigital, setCountedDigital] = useState("");
  const [closeResult, setCloseResult] = useState<any>(null);

  const handleCloseCount = (e: React.FormEvent) => {
    e.preventDefault();
    const countedTot = Number(countedPhysical) + Number(countedDigital);
    const diff = countedTot - expectedTotal;
    setCloseResult({ countedTotal: countedTot, diff });
  };

  const confirmClose = (notes: string) => {
    closeCashRegister.mutate(
      {
        expectedTotal,
        countedTotal: closeResult.countedTotal,
        diff: closeResult.diff,
        notes,
      },
      {
        onSuccess: () => {
          setIsOpen(false);
          setCloseResult(null);
          setCountedPhysical("");
          setCountedDigital("");
        },
      },
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="sm:max-w-[500px] bg-slate-900 border-slate-700 text-slate-100">
        <DialogHeader>
          <DialogTitle>Fechamento de Caixa (Gaveta Cega)</DialogTitle>
          <DialogDescription className="text-slate-400">
            Conte os valores em gaveta e maquininhas e informe abaixo. O sistema
            verificará as diferenças.
          </DialogDescription>
        </DialogHeader>

        {pendingCommissions > 0 && (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-amber-500 text-sm mt-2 flex gap-2">
            <AlertTriangle size={18} className="shrink-0" />
            <p>
              Aviso: Existem <strong>R$ {pendingCommissions.toFixed(2)}</strong>{" "}
              em comissões pendentes hoje. (Fechamento Flexível permitido)
            </p>
          </div>
        )}

        {!closeResult ? (
          <form onSubmit={handleCloseCount} className="space-y-4 pt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Dinheiro Físico (R$)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={countedPhysical}
                  onChange={(e) => setCountedPhysical(e.target.value)}
                  className="bg-slate-800 border-slate-700 text-emerald-400 font-bold"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Digital (Pix/Cartões) (R$)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={countedDigital}
                  onChange={(e) => setCountedDigital(e.target.value)}
                  className="bg-slate-800 border-slate-700 text-blue-400 font-bold"
                  required
                />
              </div>
            </div>
            <Button
              type="submit"
              className="w-full bg-blue-600 hover:bg-blue-700 font-bold"
            >
              Conferir Totais
            </Button>
          </form>
        ) : (
          <div className="space-y-4 pt-4">
            {Math.abs(closeResult.diff) < 0.01 ? (
              <div className="bg-emerald-500/20 text-emerald-400 p-4 rounded-lg text-center flex flex-col items-center border border-emerald-500/30">
                <CheckCircle size={32} className="mb-2" />
                <p className="font-bold">CONFRONTO EXATO</p>
                <p className="text-sm">
                  Os valores contados batem perfeitamente com as transações.
                </p>
              </div>
            ) : (
              <div
                className={`p-4 rounded-lg text-center border flex flex-col items-center ${
                  closeResult.diff < 0
                    ? "bg-red-500/20 text-red-400 border-red-500/30"
                    : "bg-cyan-500/20 text-cyan-400 border-cyan-500/30"
                }`}
              >
                <AlertTriangle size={32} className="mb-2" />
                <p className="font-bold">
                  {closeResult.diff < 0
                    ? "QUEBRA DE CAIXA (FALTA)"
                    : "SOBRA DE CAIXA"}
                </p>
                <p className="text-sm">
                  Diferença de R$ {Math.abs(closeResult.diff).toFixed(2)}{" "}
                  detectada no fechamento.
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label>Observações / Justificativa</Label>
              <Input
                id="closing-notes"
                className="bg-slate-800 border-slate-700"
                placeholder={
                  closeResult.diff !== 0
                    ? "Explique a divergência..."
                    : "Tudo certo"
                }
              />
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <Button variant="ghost" onClick={() => setCloseResult(null)}>
                Voltar e Recontar
              </Button>
              <Button
                onClick={() =>
                  confirmClose(
                    (
                      document.getElementById(
                        "closing-notes",
                      ) as HTMLInputElement
                    ).value,
                  )
                }
                className="bg-red-600 hover:bg-red-700"
                disabled={closeCashRegister.isPending}
              >
                {closeCashRegister.isPending
                  ? "Encerrando..."
                  : "Encerrar Turno"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function ExpenseModal({
  isOpen,
  setIsOpen,
  registerExpense,
}: {
  isOpen: boolean;
  setIsOpen: (val: boolean) => void;
  registerExpense: any;
}) {
  const [expenseDescription, setExpenseDescription] = useState("");
  const [expenseAmount, setExpenseAmount] = useState("");
  const [expenseCategory, setExpenseCategory] = useState("GERAL");
  const [expenseMethod, setExpenseMethod] = useState("DINHEIRO");

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="sm:max-w-[425px] bg-slate-900 border-slate-700 text-slate-100">
        <DialogHeader>
          <DialogTitle>Lançar Despesa / Saída</DialogTitle>
          <DialogDescription className="text-slate-400">
            Registre uma despesa para descontar do caixa atual.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-4">
          <div className="space-y-2">
            <Label>Descrição</Label>
            <Input
              value={expenseDescription}
              onChange={(e) => setExpenseDescription(e.target.value)}
              placeholder="Ex: Compra de produtos, Luz..."
              className="bg-slate-800 border-slate-700"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Valor (R$)</Label>
              <Input
                type="number"
                step="0.01"
                min="0.01"
                value={expenseAmount}
                onChange={(e) => setExpenseAmount(e.target.value)}
                placeholder="0.00"
                className="bg-slate-800 border-slate-700 text-red-400 font-bold"
              />
            </div>
            <div className="space-y-2">
              <Label>Método</Label>
              <select
                value={expenseMethod}
                onChange={(e) => setExpenseMethod(e.target.value)}
                className="w-full bg-slate-800 border border-slate-700 text-slate-100 h-10 px-3 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="DINHEIRO">Dinheiro</option>
                <option value="PIX">Pix</option>
                <option value="TRANSFERENCIA">Transferência</option>
                <option value="CARTÃO DE CRÉDITO">Cartão de Crédito</option>
                <option value="CARTÃO DE DÉBITO">Cartão de Débito</option>
              </select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Categoria</Label>
            <select
              value={expenseCategory}
              onChange={(e) => setExpenseCategory(e.target.value)}
              className="w-full bg-slate-800 border border-slate-700 text-slate-100 h-10 px-3 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="GERAL">Geral</option>
              <option value="FORNECEDORES">Fornecedores</option>
              <option value="OPERAÇÃO">Operação (Água, Luz, Internet)</option>
              <option value="PESSOAL">Pessoal / Comissões</option>
              <option value="OUTROS">Outros</option>
            </select>
          </div>

          <Button
            className="w-full bg-red-600 hover:bg-red-700 font-bold mt-4"
            disabled={
              registerExpense.isPending || !expenseAmount || !expenseDescription
            }
            onClick={() =>
              registerExpense.mutate(
                {
                  description: expenseDescription,
                  amount: expenseAmount,
                  category: expenseCategory,
                  method: expenseMethod,
                },
                {
                  onSuccess: () => {
                    setIsOpen(false);
                    setExpenseDescription("");
                    setExpenseAmount("");
                    setExpenseCategory("GERAL");
                    setExpenseMethod("DINHEIRO");
                  },
                },
              )
            }
          >
            {registerExpense.isPending ? "Lançando..." : "Confirmar Saída"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
